import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { AgentStatus } from '../api'
import { EditIcon } from '../icons/EditIcon.tsx'
import { RefreshIcon } from '../icons/RefreshIcon.tsx'
import { Message, useVelaStore } from '../store'
import FeedbackButtons from './FeedbackButtons'
import FlightCardList from './flight/FlightCardList.tsx'
import HotelCardList from './HotelCardList'

const renderFeedbackAndTrace = (
  message: Message,
  isUser: boolean,
  langfuseDebugMode: boolean,
  langfuseBaseUrl: string,
  messageHandler?: (message: Message) => void,
): ReactElement | null => {
  if (isUser) {
    return (
      <button
        onClick={() => messageHandler?.(message)}
        style={{
          marginLeft: '8px',
          padding: '4px',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--vela-text-color)',
          opacity: 0.7,
        }}
        aria-label="Edit message"
      >
        <EditIcon />
      </button>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <FeedbackButtons messageId={message.traceIds?.at(-1) || message.id} />
      <button
        onClick={() => messageHandler?.(message)}
        style={{
          marginLeft: '8px',
          padding: '4px',
          marginRight: '8px',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--vela-text-color)',
          opacity: 0.7,
          display: 'flex',
          alignItems: 'center',
        }}
        aria-label="Refresh response"
      >
        <RefreshIcon />
      </button>
      {langfuseDebugMode && message.traceIds && message.traceIds.length > 0 && (
        <div style={{ display: 'flex', marginLeft: '8px' }}>
          {message.traceIds.map((traceId, index) => (
            <a
              key={traceId}
              href={`${langfuseBaseUrl}/traces?peek=${traceId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '3px 6px',
                marginLeft: '4px',
                fontSize: '10px',
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                display: 'inline-block',
              }}
            >
              trace{index + 1}
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

const getAgentDisplayName = (agentType: AgentStatus['currentAgent']): string => {
  switch (agentType) {
    case 'PLANNER':
      return 'Planning Agent'
    case 'FLIGHT_EXACT':
      return 'Flight Search Agent (Exact Dates)'
    case 'FLIGHT_FLEXIBLE':
      return 'Flight Search Agent (Flexible Dates)'
    case 'HOTEL_EXACT':
      return 'Hotel Search Agent (Exact Dates)'
    case 'HOTEL_FLEXIBLE':
      return 'Hotel Search Agent (Flexible Dates)'
    case 'CHAT_RESPONSE':
      return 'Chat Agent'
    default:
      return 'Agent'
  }
}

const getStateDisplayText = (state: AgentStatus['state']): string => {
  switch (state) {
    case 'PROCESSING':
      return 'Processing...'
    case 'API_CALL':
      return 'Searching...'
    case 'COMPLETED':
      return 'Completed'
    case 'ERROR':
      return 'Error'
    default:
      return 'Working...'
  }
}

const ChatUI: React.FC = () => {
  const settings = useVelaStore(state => state.settings)
  const messages = useVelaStore(state => state.messages)
  const isLoading = useVelaStore(state => state.isLoading)
  const currentAgentStatus = useVelaStore(state => state.currentAgentStatus)
  const sendMessage = useVelaStore(state => state.sendMessage)
  const deleteMessagesFrom = useVelaStore(state => state.deleteMessagesFrom)

  const [inputValue, setInputValue] = useState<string>('')
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState<string>('')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const editInputRef = useRef<HTMLInputElement>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input field when component mounts
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Handle message submission
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const trimmedValue = inputValue.trim()
    if (trimmedValue) {
      void sendMessage(trimmedValue)
      setInputValue('')
    }
  }

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value)
  }

  // Handle edit button click
  const handleEdit = (message: Message): void => {
    setEditingMessageId(message.id)
    setEditingText(message.text)

    // Focus the edit input after it's rendered
    setTimeout(() => {
      editInputRef.current?.focus()
    }, 0)
  }

  // Handle edit cancel
  const handleEditCancel = (): void => {
    setEditingMessageId(null)
    setEditingText('')
  }

  // Handle edit save
  const handleEditSave = (message: Message): void => {
    if (editingText.trim() === '') return

    deleteMessagesFrom(message.id)

    // Send the edited message
    void sendMessage(editingText, message.previousMessageId)

    // Reset editing state
    setEditingMessageId(null)
    setEditingText('')
  }

  // Handle refresh button click
  const handleRefresh = (message: Message): void => {
    const previousMessageId = message.previousMessageId
    if (!previousMessageId) return

    const previousMessage = messages.find(msg => msg.id === previousMessageId)
    if (!previousMessage) return

    deleteMessagesFrom(previousMessage.id)

    void sendMessage(previousMessage.text, previousMessageId)
  }

  // Render a message bubble
  const renderMessage = (message: Message): ReactElement => {
    const isUser = message.sender === 'user'
    const messageHandler = isUser ? handleEdit : handleRefresh

    if (message.isCard && message.flightParams && message.flightSearchResults) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <FlightCardList
            key={message.id}
            flightSearchParameters={message.flightParams}
            flightSearchResults={message.flightSearchResults}
            messageText={message.text}
          />
          {renderFeedbackAndTrace(
            message,
            isUser,
            settings.langfuseDebugMode,
            settings.langfuseBaseUrl,
            messageHandler,
          )}
        </div>
      )
    }

    if (
      message.isCard &&
      message.hotelResults &&
      message.hotelResults.hotels &&
      message.hotelResults.hotels.length > 0
    ) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div
            key={message.id}
            className="chat-card"
            style={{
              margin: '10px 0',
              alignSelf: 'flex-start',
              width: '100%',
              maxWidth: '300px',
            }}
          >
            <HotelCardList hotelResults={message.hotelResults} />
            <div
              style={{
                padding: '0 8px',
                marginTop: '8px',
                fontSize: '14px',
                color: 'var(--vela-text-color)',
              }}
            >
              {message.text}
            </div>
          </div>
          {renderFeedbackAndTrace(
            message,
            isUser,
            settings.langfuseDebugMode,
            settings.langfuseBaseUrl,
            messageHandler,
          )}
        </div>
      )
    }

    if (message.isCard) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div
            key={message.id}
            className="chat-card"
            style={{
              margin: '10px 0',
              padding: '12px',
              backgroundColor: 'var(--vela-primary-color)',
              color: 'white',
              borderRadius: '8px',
              maxWidth: '80%',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              alignSelf: 'flex-start',
            }}
          >
            {message.text}
          </div>
          {renderFeedbackAndTrace(
            message,
            isUser,
            settings.langfuseDebugMode,
            settings.langfuseBaseUrl,
            messageHandler,
          )}
        </div>
      )
    }

    // If this is a user message being edited
    if (isUser && editingMessageId === message.id) {
      return (
        <div
          key={message.id}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            margin: '10px 0',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '80%',
            }}
          >
            <form
              onSubmit={e => {
                e.preventDefault()
                if (editingText.trim()) {
                  handleEditSave(message)
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <input
                ref={editInputRef}
                type="text"
                value={editingText}
                onChange={e => setEditingText(e.target.value)}
                style={{
                  padding: '12px',
                  borderRadius: '18px',
                  border: '1px solid var(--vela-border-color)',
                  marginBottom: '8px',
                  backgroundColor: '#fff',
                  color: 'var(--vela-text-color)',
                  outline: 'none',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleEditCancel}
                  style={{
                    padding: '6px 12px',
                    marginRight: '8px',
                    backgroundColor: '#f0f0f0',
                    color: 'var(--vela-text-color)',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!editingText.trim()}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--vela-primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: editingText.trim() ? 'pointer' : 'not-allowed',
                    opacity: editingText.trim() ? 1 : 0.7,
                  }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )
    }

    return (
      <div
        key={message.id}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
        }}
      >
        <div
          className={`chat-bubble ${isUser ? 'user' : 'agent'}`}
          style={{
            margin: '10px 0',
            padding: '12px',
            backgroundColor: isUser ? 'var(--vela-primary-color)' : '#f0f0f0',
            color: isUser ? 'white' : 'var(--vela-text-color)',
            borderRadius: '18px',
            maxWidth: '80%',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            alignSelf: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          {message.text}
        </div>
        {renderFeedbackAndTrace(
          message,
          isUser,
          settings.langfuseDebugMode,
          settings.langfuseBaseUrl,
          messageHandler,
        )}
      </div>
    )
  }

  return (
    <div
      className="chat-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        className="messages-container"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {messages.length === 0 ? (
          <div
            className="empty-state"
            style={{
              textAlign: 'center',
              margin: 'auto',
              color: 'var(--vela-text-color)',
              opacity: 0.7,
            }}
          >
            <p>Start a conversation by typing a message below.</p>
          </div>
        ) : (
          messages.map(renderMessage)
        )}

        {isLoading && (
          <div
            className="loading-indicator"
            style={{
              padding: '10px',
              alignSelf: 'flex-start',
              width: '100%',
            }}
          >
            {currentAgentStatus ? (
              <div
                style={{
                  backgroundColor: '#f0f0f0',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor:
                          currentAgentStatus.state === 'ERROR'
                            ? '#ff4444'
                            : currentAgentStatus.state === 'COMPLETED'
                            ? '#44bb44'
                            : '#4444ff',
                        marginRight: '8px',
                        animation:
                          currentAgentStatus.state === 'PROCESSING' ||
                          currentAgentStatus.state === 'API_CALL'
                            ? 'pulse 1.5s infinite'
                            : 'none',
                      }}
                    />
                    <span
                      style={{
                        fontWeight: 500,
                        color: 'var(--vela-text-color)',
                      }}
                    >
                      {getAgentDisplayName(currentAgentStatus.currentAgent)}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '14px',
                      color: 'var(--vela-text-color)',
                      opacity: 0.7,
                    }}
                  >
                    {currentAgentStatus.progressPercentage}%
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      color:
                        currentAgentStatus.state === 'ERROR' ? '#ff4444' : 'var(--vela-text-color)',
                      opacity: 0.8,
                    }}
                  >
                    {currentAgentStatus.state === 'ERROR' && currentAgentStatus.message
                      ? currentAgentStatus.message
                      : getStateDisplayText(currentAgentStatus.state)}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: '8px',
                    height: '4px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      backgroundColor:
                        currentAgentStatus.state === 'ERROR'
                          ? '#ff4444'
                          : currentAgentStatus.state === 'COMPLETED'
                          ? '#44bb44'
                          : '#4444ff',
                      width: `${currentAgentStatus.progressPercentage}%`,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            ) : (
              <div
                style={{
                  color: 'var(--vela-text-color)',
                  opacity: 0.7,
                }}
              >
                <span>Agent is typing...</span>
              </div>
            )}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          padding: '10px',
          borderTop: '1px solid var(--vela-border-color)',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Type a message..."
          style={{
            flex: 1,
            padding: '12px',
            borderRadius: '20px',
            border: '1px solid var(--vela-border-color)',
            backgroundColor: '#fff',
            color: 'var(--vela-text-color)',
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={!inputValue || isLoading}
          style={{
            marginLeft: '8px',
            padding: '0 16px',
            backgroundColor: 'var(--vela-primary-color)',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            cursor: inputValue && !isLoading ? 'pointer' : 'not-allowed',
            opacity: inputValue && !isLoading ? 1 : 0.7,
          }}
        >
          Send
        </button>
      </form>
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  )
}

export default ChatUI
